import { useState, useEffect } from 'react';
import { useAccount, useNetwork } from 'wagmi';
import { Label } from '@/components/ui/label';
import { initializeFirebase } from '@/lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface UserToken {
  address: string;
  name: string;
  symbol: string;
}

interface UserTokenSelectorProps {
  onSelectToken: (tokenAddress: string) => void;
  selectedToken?: string;
  label?: string;
  showLabel?: boolean;
}

/**
 * Reusable component to show user's created tokens
 * Fetches tokens from Firebase for the connected wallet and current chain
 */
export function UserTokenSelector({ 
  onSelectToken, 
  selectedToken = '', 
  label = 'Your Created Tokens',
  showLabel = true 
}: UserTokenSelectorProps) {
  const { address } = useAccount();
  const { chain } = useNetwork();
  const [userTokens, setUserTokens] = useState<UserToken[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load tokens owned by the connected wallet for the current chain
  useEffect(() => {
    let isMounted = true;

    const loadUserTokens = async () => {
      if (!address || !chain?.id) {
        if (isMounted) {
          setUserTokens([]);
        }
        return;
      }

      setIsLoading(true);

      try {
        const { db } = await initializeFirebase();
        const tokensRef = collection(db, 'tokens');
        const q = query(
          tokensRef,
          where('owner', '==', address),
          where('chainId', '==', chain.id)
        );

        const snapshot = await getDocs(q);
        const list = snapshot.docs
          .map((doc) => {
            const data = doc.data() as any;
            return {
              address: data.address || '',
              name: data.name || '',
              symbol: data.symbol || '',
            };
          })
          .filter((t) => t.address);

        if (isMounted) {
          setUserTokens(list);
        }
      } catch (error) {
        console.error('Failed to load user tokens:', error);
        if (isMounted) {
          setUserTokens([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUserTokens();

    return () => {
      isMounted = false;
    };
  }, [address, chain?.id]);

  if (!address || userTokens.length === 0) {
    return null; // Don't show if not connected or no tokens
  }

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{label}</Label>
          <span className="text-[11px] text-muted-foreground">
            {isLoading
              ? 'Loading…'
              : `${userTokens.length} token${userTokens.length !== 1 ? 's' : ''} on ${chain?.name ?? 'this network'}`}
          </span>
        </div>
      )}
      
      <select
        value={selectedToken}
        onChange={(e) => onSelectToken(e.target.value)}
        className="border border-input bg-background rounded-md px-3 py-2 text-sm w-full hover:bg-accent transition-colors"
        disabled={isLoading}
      >
        <option value="">
          {isLoading ? 'Loading your tokens...' : 'Select one of your created tokens'}
        </option>
        {userTokens.map((token) => (
          <option key={token.address} value={token.address}>
            {token.name || token.symbol || token.address.slice(0, 10)}
            {token.symbol ? ` (${token.symbol})` : ''}
          </option>
        ))}
      </select>
      
      {!isLoading && userTokens.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Quick access to tokens you created in this app
        </p>
      )}
    </div>
  );
}
